import React, { useEffect, useRef, ReactElement } from "react";
import useHeaderStyles, { HeaderClasses } from "./styles";
import logo from "../../../assets/img/logo.png";
import {
  ArrowBottomIcon,
  EnIcon,
  TrIcon,
  MenuIcon,
  CloseIcon,
  HeaderBillPaymentIcon,
  HeaderGamePinIcon,
  HeaderWalletIcon,
  HeaderPaymentIcon,
  PrepaidIcon,
  TravelIcon,
} from "../../../assets/img/svgicons";
import { gsap, TimelineMax } from "gsap";
import LanguageSelection from "../../LanguageSelection";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import SubMenuItems from "./SubMenuItems";

export type HeaderProps = {};

function Header(): ReactElement {
  // Translation
  const { t } = useTranslation();
  // Calculate Screen Width
  let width = window.innerWidth;
  let breakpoint = 768;
  //  Animation Controller
  let menuAnimation = new TimelineMax({ paused: true });
  let menuAnimationBack = new TimelineMax({ paused: true });
  let hasHeaderAnimation: boolean = false;
  // Reference Element
  const headerContainerRef = useRef<HTMLDivElement>(null);
  const mobileMenuRef = useRef<HTMLElement>(null);
  const menuButtonRef = useRef<HTMLButtonElement>(null);
  const mounted = useRef();
  // Statement
  const [showMenuMoile, setShowMenuMoile] = React.useState(false);
  const [showMenu, setShowMenu] = React.useState(false);
  // Styles Hook Calling
  const useStyles: HeaderClasses = useHeaderStyles({ showMenuMoile });

  const openMenuHandler = () => {
    document.body.style.overflowY = "hidden";
    setShowMenuMoile(true);
    menuAnimation.restart();
  };
  const closeMenuHandler = () => {
    setShowMenu(false);
    document.body.style.overflowY = "scroll";
    setShowMenuMoile(false);
    menuAnimationBack.restart();
  };

  const showMenuHandler = () => {
    setShowMenu(true);
  };
  const hideMenuHandler = () => {
    setShowMenu(false);
  };

  const showMenuToggleHandler = () => setShowMenu((data) => !data);

  useEffect(() => {
    if (width > breakpoint) {
      hasHeaderAnimation = true;
    } else {
      hasHeaderAnimation = false;
    }
  }, [width, breakpoint]);

  useEffect(() => {
    if (showMenuMoile === true) {
      document.body.style.overflowY = "hidden";
    } else {
      document.body.style.overflowY = "scroll";
    }
    if (!mounted.current) {
      if (mobileMenuRef && mobileMenuRef.current) {
        menuAnimation.to(mobileMenuRef.current, 0.6, {
          x: -90,
        });
        menuAnimationBack.to(mobileMenuRef.current, 0.6, {
          transform: "translateX(-100%)",
        });
      }
    }
  });

  useEffect(() => {
    if (
      headerContainerRef &&
      headerContainerRef.current &&
      hasHeaderAnimation === true
    ) {
      // header sticky animation
      let circleImageAnimation = gsap.timeline({
        scrollTrigger: {
          trigger: headerContainerRef.current,
          start: "center 10px",
          end: "bottom",
          toggleActions: "pause play pause reverse",
        },
      });
      circleImageAnimation.to(headerContainerRef.current, {
        padding: 16,
        duration: 0.3,
      });
    }
  }, []);

  return (
    <>
      <div className="noAction"></div>
      <nav className={useStyles.menuOpened} ref={mobileMenuRef}>
        <Link to="/" className={useStyles.mobileMenuLogoBox}>
          <img src={logo} alt="logo" className={useStyles.logo} />
        </Link>
        <button
          className={useStyles.closeBtn}
          ref={menuButtonRef}
          onClick={closeMenuHandler}
        >
          <CloseIcon width="40px" />
        </button>
        <ul className={useStyles.mobileMenuList}>
          <li
            className={`${useStyles.mobileMenuItem} ${useStyles.openedMenu} ${useStyles.mobileDynamicItem}`}
            onClick={() => showMenuToggleHandler()}
          >
            <div className={useStyles.mobileMenuItemLink}>
              {t("headerServicesText")}
              <ArrowBottomIcon
                width="14px"
                style={
                  showMenu
                    ? {
                        marginLeft: "4px",
                        transform: "rotate(180deg)",
                        transition: ".2s all",
                      }
                    : {
                        marginLeft: "4px",
                        transition: ".2s all",
                      }
                }
              />
            </div>
          </li>
          <div
            className={useStyles.mobileServicesBox}
            style={showMenu ? { display: "block" } : {}}
          >
            <ul>
              <Link to="/services/bill-payment" onClick={closeMenuHandler}>
                <li className={useStyles.moileServicesItem}>
                  {t("billPaymentService")}
                </li>
              </Link>
              <Link to="/services/game-pin" onClick={closeMenuHandler}>
                <li className={useStyles.moileServicesItem}>
                  {t("gamePinService")}
                </li>
              </Link>
              <Link to="/services/travel" onClick={closeMenuHandler}>
                <li className={useStyles.moileServicesItem}>
                  {t("travelService")}
                </li>
              </Link>
              <Link to="/services/prepaid" onClick={closeMenuHandler}>
                <li className={useStyles.moileServicesItem}>
                  {t("prepaidService")}
                </li>
              </Link>
            </ul>
          </div>
          <li className={useStyles.mobileMenuItem}>
            <a href="#" className={useStyles.mobileMenuItemLink}>
              {t("headerFeesAndLimitsText")}
            </a>
          </li>
          <li className={useStyles.mobileMenuItem}>
            <a href="#" className={useStyles.mobileMenuItemLink}>
              {t("headerBusinessText")}
            </a>
          </li>
        </ul>
      </nav>
      <header className={useStyles.header}>
        <div className={useStyles.headerWrapper}>
          <div className={useStyles.container} ref={headerContainerRef}>
            <div className={useStyles.headerLeftSide}>
              <button
                className={useStyles.menuBtn}
                ref={menuButtonRef}
                onClick={openMenuHandler}
              >
                <MenuIcon width="20px" />
              </button>
              <Link to="/" className="logo-box">
                <img src={logo} alt="logo" className={useStyles.logo} />
              </Link>
              <ul className={useStyles.menuListItems}>
                <li
                  className={`${useStyles.menuItem} ${useStyles.openedMenu}`}
                  onMouseEnter={() => showMenuHandler()}
                  onMouseLeave={() => hideMenuHandler()}
                >
                  <a href="#" className={useStyles.menuItemLink}>
                    <span className={useStyles.menuItemText}>
                      {t("headerServicesText")}
                    </span>
                    <ArrowBottomIcon
                      width="8px"
                      style={
                        showMenu
                          ? {
                              transform: "rotate(180deg)",
                              transition: ".2s all",
                            }
                          : {
                              transition: ".2s all",
                              paddingTop: 1,
                            }
                      }
                    />
                  </a>
                </li>
                <div
                  className={`${useStyles.servicesMenuBox} ${
                    showMenu ? useStyles.openedMenu : ""
                  }`}
                  onMouseEnter={() => showMenuHandler()}
                  onMouseLeave={() => hideMenuHandler()}
                >
                  <SubMenuItems
                    menuItems={[
                      {
                        icon: (
                          <HeaderBillPaymentIcon
                            width="15"
                            style={{ marginRight: 6 }}
                          />
                        ),
                        label: t("billPaymentService"),
                        link: "/services/bill-payment",
                      },
                      {
                        icon: (
                          <HeaderGamePinIcon
                            width="18"
                            style={{ marginRight: 11 }}
                          />
                        ),
                        label: t("gamePinService"),
                        link: "/services/game-pin",
                      },
                      {
                        icon: (
                          <TravelIcon width="20" style={{ marginRight: 3 }} />
                        ),
                        label: t("travelService"),
                        link: "/services/travel",
                      },
                      {
                        icon: (
                          <PrepaidIcon width="14" style={{ marginRight: 12 }} />
                        ),
                        label: t("prepaidService"),
                        link: "/services/prepaid",
                      },
                    ]}
                  />
                </div>
                <li className={useStyles.menuItem}>
                  <a href="#" className={useStyles.menuItemLink}>
                    <span className={useStyles.menuItemText}>
                      {t("headerFeesAndLimitsText")}
                    </span>
                  </a>
                </li>
                <li className={useStyles.menuItem}>
                  <a href="#" className={useStyles.menuItemLink}>
                    <span className={useStyles.menuItemText}>
                      {t("headerBusinessText")}
                    </span>
                  </a>
                </li>
              </ul>
            </div>
            <div className={useStyles.headerRightSide}>
              {/* <LanguageSelection
                languages={[
                  { lang: "tr", icon: <TrIcon width="21" />, label: "Turkish" },
                  {
                    lang: "en",
                    icon: <EnIcon />,
                    label: "English",
                    activeLang: true,
                  },
                ]}
              /> */}
            </div>
          </div>
        </div>
      </header>
    </>
  );
}
export default Header;
